import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, TagsInput, Badge } from '@mantine/core';
import { IconMail, IconInfoCircle } from '@tabler/icons-react';
import PageHeader from '@/components/page-header';
import SettingsLayout from '@/components/settings-layout';
import SettingsCard from '@/components/settings-card';
import { localTenant, updateMe } from '@/services/session-service';
import { Tenant } from '@turizi/shared';
import { setWebTitle } from '@/lib/format-utils';
import { useUpdateTenant } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';

export default function SettingsEmailsPage(): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();

    const updateTenantMutation = useUpdateTenant();
    const [draft, setDraft] = useState<Tenant | null>(tenant);

    setWebTitle(t('emails_settings'));

    const handleSavePrimaryEmail = () => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            settings: { email: primaryEmail }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('settings_saved_success'), t('success'));
            },
            onError: (err: any) => {
                notify.error(err?.message || t('error_saving_settings'), t('error'));
            }
        });
    };

    const handleSaveSenderEmails = () => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            settings: { sender_emails: senderEmailsList }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('settings_saved_success'), t('success'));
            },
            onError: (err: any) => {
                notify.error(err?.message || t('error_saving_settings'), t('error'));
            }
        });
    };

    const updateSettingValue = (field: keyof Tenant['settings'], value: any) => {
        setDraft((prev) => (prev ? { ...prev, settings: { ...prev.settings, [field]: value } } : null));
    };

    const senderEmailsList = draft?.settings?.sender_emails || [];
    const primaryEmail = draft?.settings?.email || '';

    return (
        <div className="panel-content max-w-5xl">
            <PageHeader title={t('settings')} />

            <SettingsLayout>
                <div className="flex flex-col gap-6 w-full">
                    {/* Informational Callout Banner */}
                    <div className="bg-blue-50/80 border border-blue-200/80 rounded-xl p-4 flex items-start gap-3 text-blue-950">
                        <div className="p-2 rounded-lg bg-blue-100/80 text-blue-700 shrink-0">
                            <IconInfoCircle size={20} />
                        </div>
                        <div className="text-xs leading-relaxed">
                            <div className="font-bold text-sm text-blue-900 mb-0.5">
                                {t('emails_settings')}
                            </div>
                            <div>
                                {t('emails_settings_desc')}
                            </div>
                        </div>
                    </div>

                    {/* Primary Organization Email Card */}
                    <SettingsCard
                        title={t('primary_email_label')}
                        description={t('primary_email_desc')}
                        onSave={handleSavePrimaryEmail}
                        saving={updateTenantMutation.isPending}
                    >
                        <TextInput
                            label={t('primary_email_label')}
                            description={t('primary_email_desc')}
                            placeholder="contacto@empresa.com"
                            type="email"
                            value={primaryEmail}
                            onChange={(e) => updateSettingValue('email', e.target.value)}
                        />
                    </SettingsCard>

                    {/* Multiple Authorized Sender Emails Card */}
                    <SettingsCard
                        title={t('sender_emails_settings')}
                        description={t('sender_emails_settings_desc')}
                        onSave={handleSaveSenderEmails}
                        saving={updateTenantMutation.isPending}
                    >
                        <div className="space-y-4">
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 leading-relaxed">
                                {t('sender_emails_help_text')}
                            </div>

                            <TagsInput
                                label={t('sender_emails_label')}
                                placeholder={t('sender_email_placeholder')}
                                value={senderEmailsList}
                                onChange={(val) => updateSettingValue('sender_emails', val)}
                                clearable
                            />

                            {/* Active Authorized Emails Summary Badges */}
                            <div className="pt-2">
                                <div className="text-xs font-semibold text-gray-700 mb-2">
                                    {t('authorized_emails_count', { count: (primaryEmail ? 1 : 0) + senderEmailsList.length })}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {primaryEmail && (
                                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-medium">
                                            <IconMail size={14} />
                                            <span>{primaryEmail}</span>
                                            <Badge size="xs" color="gray" variant="light" className="ml-1">
                                                {t('primary')}
                                            </Badge>
                                        </div>
                                    )}
                                    {senderEmailsList.map((email: string) => (
                                        <div
                                            key={email}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-800 border border-gray-200 rounded-lg text-xs font-medium"
                                        >
                                            <IconMail size={14} className="text-gray-500" />
                                            <span>{email}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </SettingsCard>
                </div>
            </SettingsLayout>
        </div>
    );
}
